import { isMaxSnap, SNAPS, tileSnaps, WINDOW_GAP, type DeskSize, type WindowPlace, type WindowRect, type WindowSnap } from '@tomo/ui';

/** The collection view a window was opened from: previous and next step through it. */
export interface WindowSource {
  collectionId: string;
  viewId: string;
}

/** A record open in a window. */
export interface RecordWindow {
  id: string;
  recordId: string;
  /** The free box (px on the desk). Kept while snapped, so the window can return to it. */
  rect: WindowRect;
  snap: WindowSnap | null;
  minimized: boolean;
  from: WindowSource | null;
}

export interface WindowsState {
  /** In opening order, which keeps each window's place in the DOM (and its editor) steady. */
  wins: RecordWindow[];
  /** Stacking order, bottom to top. */
  order: string[];
  /** The window that has the keyboard, or null while the page has it. */
  active: string | null;
  /** Bumped to move focus into a window (opening it, picking it in the taskbar). */
  focus: { id: string; n: number } | null;
  /** The desk's measured size (0 until the desk is on screen). */
  desk: DeskSize;
}

const CASCADE = 28;
const DEFAULT_SIZE = { w: 760, h: 720 };
const EMPTY: WindowsState = { wins: [], order: [], active: null, focus: null, desk: { width: 0, height: 0 } };

/** A desk narrower than this (phones) shows each window full size, with no moving or arranging. */
export const isCompactDesk = (desk: DeskSize): boolean => desk.width > 0 && desk.width < 640;

/** The record in the window that has the keyboard (the collaborator presence location). */
export const activeRecordId = (s: WindowsState): string | null => s.wins.find((w) => w.id === s.active && !w.minimized)?.recordId ?? null;

function topVisible(wins: RecordWindow[], order: string[]): string | null {
  const visible = new Set(wins.filter((w) => !w.minimized).map((w) => w.id));
  for (let i = order.length - 1; i >= 0; i--) if (visible.has(order[i]!)) return order[i]!;
  return null;
}

/**
 * The record windows of one project: which are open, where, and which one has the keyboard.
 * Saved on this device (`storageKey`), so windows come back after a reload.
 */
export class WindowManager {
  private state: WindowsState;
  private listeners = new Set<() => void>();
  private focusCount = 0;
  /** Where focus was when each window opened, to return to when the last one goes away. */
  private openers = new Map<string, HTMLElement>();

  constructor(private storageKey: string | null = null) {
    this.state = { ...EMPTY, ...load(storageKey) };
  }

  get = (): WindowsState => this.state;

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => void this.listeners.delete(listener);
  };

  private set(next: WindowsState) {
    const prev = this.state;
    this.state = next;
    if (next.wins !== prev.wins || next.order !== prev.order || next.active !== prev.active) save(this.storageKey, next);
    this.listeners.forEach((l) => l());
  }

  private bump(id: string) {
    return { id, n: ++this.focusCount };
  }

  private update(id: string, change: (w: RecordWindow) => RecordWindow) {
    const s = this.state;
    if (!s.wins.some((w) => w.id === id)) return;
    this.set({ ...s, wins: s.wins.map((w) => (w.id === id ? change(w) : w)) });
  }

  setDesk(desk: DeskSize) {
    const s = this.state;
    if (desk.width !== s.desk.width || desk.height !== s.desk.height) this.set({ ...s, desk });
  }

  /** Opens a record in a new window, or brings the window already showing it to the front. */
  open(recordId: string, from: WindowSource | null = null) {
    const s = this.state;
    const existing = s.wins.find((w) => w.recordId === recordId);
    if (existing) {
      if (from) this.update(existing.id, (w) => ({ ...w, from }));
      return this.activate(existing.id, true);
    }
    let id = '';
    while (!id || s.wins.some((w) => w.id === id)) id = `w${Math.random().toString(36).slice(2, 9)}`;
    this.rememberOpener(id);
    const win: RecordWindow = { id, recordId, rect: this.nextRect(), snap: null, minimized: false, from };
    this.set({ ...s, wins: [...s.wins, win], order: [...s.order, id], active: id, focus: this.bump(id) });
  }

  close(id: string) {
    const s = this.state;
    if (!s.wins.some((w) => w.id === id)) return;
    const wins = s.wins.filter((w) => w.id !== id);
    const order = s.order.filter((o) => o !== id);
    const wasActive = s.active === id;
    const active = wasActive ? topVisible(wins, order) : s.active;
    const opener = this.openers.get(id);
    this.openers.delete(id);
    this.set({ ...s, wins, order, active, focus: wasActive && active ? this.bump(active) : s.focus });
    if (wasActive && !active) this.returnFocus(opener);
  }

  closeAll() {
    this.openers.clear();
    this.set({ ...this.state, wins: [], order: [], active: null, focus: null });
  }

  /** Brings a window to the front (restoring it if minimized); `focus` also moves the keyboard into it. */
  activate(id: string, focus = false) {
    const s = this.state;
    const win = s.wins.find((w) => w.id === id);
    if (!win) return;
    const onTop = s.order[s.order.length - 1] === id;
    if (s.active === id && onTop && !win.minimized && !focus) return;
    this.set({
      ...s,
      wins: win.minimized ? s.wins.map((w) => (w.id === id ? { ...w, minimized: false } : w)) : s.wins,
      order: onTop ? s.order : [...s.order.filter((o) => o !== id), id],
      active: id,
      focus: focus ? this.bump(id) : s.focus,
    });
  }

  /** The page has the keyboard again (the windows stay where they are). */
  deactivate() {
    if (this.state.active) this.set({ ...this.state, active: null });
  }

  minimize(id: string) {
    const s = this.state;
    if (!s.wins.some((w) => w.id === id)) return;
    const wins = s.wins.map((w) => (w.id === id ? { ...w, minimized: true } : w));
    const wasActive = s.active === id;
    const active = wasActive ? topVisible(wins, s.order) : s.active;
    this.set({ ...s, wins, active, focus: wasActive && active ? this.bump(active) : s.focus });
    if (wasActive && !active) this.returnFocus(this.openers.get(id));
  }

  minimizeAll() {
    const s = this.state;
    this.set({ ...s, wins: s.wins.map((w) => (w.minimized ? w : { ...w, minimized: true })), active: null });
  }

  /** A taskbar click: restore and focus a window, or minimize the one in front. */
  toggle(id: string) {
    const s = this.state;
    const win = s.wins.find((w) => w.id === id);
    if (!win) return;
    if (!win.minimized && s.active === id) this.minimize(id);
    else this.activate(id, true);
  }

  /** Moves a window to a free box, or snaps it into a slot (keeping its free box for later). */
  place(id: string, place: WindowPlace) {
    this.update(id, (w) => ('snap' in place ? { ...w, snap: place.snap } : { ...w, rect: place.rect, snap: null }));
  }

  toggleMaximize(id: string) {
    this.update(id, (w) => ({ ...w, snap: isMaxSnap(w.snap) ? null : SNAPS.max }));
  }

  /** Shows another record in a window (previous/next), or brings forward the window that has it. */
  show(id: string, recordId: string) {
    const other = this.state.wins.find((w) => w.recordId === recordId);
    if (other) {
      if (other.id !== id) this.activate(other.id, true);
      return;
    }
    this.update(id, (w) => ({ ...w, recordId }));
  }

  /** Puts the visible windows side by side, in the order they were opened. */
  tile() {
    const s = this.state;
    const visible = s.wins.filter((w) => !w.minimized);
    const snaps = tileSnaps(visible.length);
    const slot = new Map(visible.map((w, i) => [w.id, snaps[i]!]));
    this.set({ ...s, wins: s.wins.map((w) => (slot.has(w.id) ? { ...w, snap: slot.get(w.id)! } : w)) });
  }

  /** Stacks the visible windows diagonally from the top left, the last opened in front. */
  cascade() {
    const s = this.state;
    const { w, h } = this.defaultSize();
    let i = 0;
    const wins = s.wins.map((win) => {
      if (win.minimized) return win;
      const offset = WINDOW_GAP * 2 + (i++ % 10) * CASCADE;
      return { ...win, snap: null, rect: { x: offset, y: offset, w, h: Math.min(h, this.desk().height - offset - WINDOW_GAP) } };
    });
    const visible = wins.filter((x) => !x.minimized).map((x) => x.id);
    const order = [...s.order.filter((o) => !visible.includes(o)), ...visible];
    this.set({ ...s, wins, order, active: visible.at(-1) ?? s.active });
  }

  /** The desk, or a guess from the viewport before it's measured (a deep link opens first). */
  private desk(): DeskSize {
    const { desk } = this.state;
    if (desk.width && desk.height) return desk;
    const vw = typeof window === 'undefined' ? 1280 : window.innerWidth;
    const vh = typeof window === 'undefined' ? 800 : window.innerHeight;
    return { width: Math.max(480, vw - 300), height: Math.max(360, vh - 120) };
  }

  /** Leaves room below for a few cascade steps. */
  private defaultSize() {
    const desk = this.desk();
    return { w: Math.min(DEFAULT_SIZE.w, desk.width - WINDOW_GAP * 2), h: Math.min(DEFAULT_SIZE.h, desk.height - WINDOW_GAP * 2 - CASCADE * 3) };
  }

  /** A new window: centered, or a step down and right from the free window in front. */
  private nextRect(): WindowRect {
    const desk = this.desk();
    const { w, h } = this.defaultSize();
    const { wins, order } = this.state;
    const frontId = topVisible(
      wins.filter((x) => !x.snap),
      order,
    );
    const front = wins.find((x) => x.id === frontId);
    if (front) {
      const x = front.rect.x + CASCADE;
      const y = front.rect.y + CASCADE;
      return x + w <= desk.width && y + h <= desk.height ? { x, y, w, h } : { x: WINDOW_GAP * 3, y: WINDOW_GAP * 3, w, h };
    }
    return { x: Math.round((desk.width - w) / 2), y: Math.round(Math.max(WINDOW_GAP, (desk.height - h) / 2)), w, h };
  }

  private rememberOpener(id: string) {
    if (typeof document === 'undefined') return;
    const el = document.activeElement;
    if (el instanceof HTMLElement && el !== document.body && !el.closest('.desk')) this.openers.set(id, el);
  }

  private returnFocus(el: HTMLElement | undefined) {
    if (el?.isConnected) el.focus({ preventScroll: true });
  }
}

/* ------------------------------------------------------------------------------ persistence */

const isRect = (r: unknown): r is WindowRect => !!r && typeof r === 'object' && ['x', 'y', 'w', 'h'].every((k) => Number.isFinite((r as Record<string, unknown>)[k]));

const isSource = (f: unknown): f is WindowSource =>
  !!f && typeof f === 'object' && typeof (f as WindowSource).collectionId === 'string' && typeof (f as WindowSource).viewId === 'string';

function isWindow(w: unknown): w is RecordWindow {
  if (!w || typeof w !== 'object') return false;
  const x = w as RecordWindow;
  return typeof x.id === 'string' && typeof x.recordId === 'string' && isRect(x.rect) && (x.snap === null || isRect(x.snap)) && typeof x.minimized === 'boolean' && (x.from === null || isSource(x.from));
}

function load(key: string | null): Pick<WindowsState, 'wins' | 'order' | 'active'> {
  if (!key) return EMPTY;
  try {
    const data = JSON.parse(localStorage.getItem(key) ?? 'null') as { wins?: unknown; order?: unknown; active?: unknown } | null;
    if (!data) return EMPTY;
    const ids = new Set<string>();
    const records = new Set<string>();
    const wins = (Array.isArray(data.wins) ? data.wins : []).filter((w): w is RecordWindow => {
      if (!isWindow(w) || ids.has(w.id) || records.has(w.recordId)) return false;
      ids.add(w.id);
      records.add(w.recordId);
      return true;
    });
    const saved = Array.isArray(data.order) ? data.order.filter((id): id is string => typeof id === 'string' && ids.has(id)) : [];
    const order = [...new Set([...saved, ...ids])];
    const active = typeof data.active === 'string' && ids.has(data.active) ? data.active : null;
    return { wins, order, active };
  } catch {
    return EMPTY;
  }
}

function save(key: string | null, s: WindowsState) {
  if (!key) return;
  try {
    if (s.wins.length) localStorage.setItem(key, JSON.stringify({ wins: s.wins, order: s.order, active: s.active }));
    else localStorage.removeItem(key);
  } catch {
    // Storage full or blocked: the windows just won't come back after a reload.
  }
}
